import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRut, formatMoneyWithCurrency } from '../../../utils/formatter';
import { documentsDic } from '../../../utils/dataDictionary';
import { CurrencyIcon } from 'src/utils/currencyIcon';
import moment from 'moment';
import FilterTabs from '../../FilterTabs/FilterTabs';
import FactoringNotice from './FactoringNotice';
import AnnulmentNotice from './AnnulmentNotice';
import CreditDebitNotice from './CreditDebitNotice';
import MatchesTab from './MatchesTab';
import NominasTab from './NominasTab';
import CommentsTab from './CommentsTab';
import ParametrizacionTab from './ParametrizacionTab';
import ExportActions from '../../export/ExportActions';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import PaymentStatusBadge from './PaymentStatusBadge';
import { dt, getDocumentDetailTabs, translateDocumentSummary } from 'src/utils/documentsI18n';
import './TaxDocumentDetail.css';

const TaxDocumentDetail = ({ data, onDocumentUpdated }) => {
  const { i18n } = useTranslation('documents');
  const [activeTab, setActiveTab] = useState('detalles');
  const [showOtherTaxesDetails, setShowOtherTaxesDetails] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const rateFromUrl = searchParams.get('rate');

  const meta = data?.meta;
  const otherTaxesTotal = meta?.otherTaxes?.reduce((sum, tax) => sum + (tax.value || 0), 0) || 0;
  const otherTaxesRate = meta?.netAmount ? (otherTaxesTotal / meta.netAmount) * 100 : 0;
  const commentCount = meta?.innerComments?.length || 0;

  const tabs = useMemo(
    () => getDocumentDetailTabs(commentCount),
    [commentCount, i18n.language]
  );

  if (!meta) {
    return (
      <div className="document-detail-container document-content tw-p-4">
        <p className="tw-text-gray-700" style={{ lineHeight: 1.5 }}>
          {dt('detail.loadError')}
        </p>
      </div>
    );
  }

  const isReceived = meta.isReceived;
  const isCreditOrDebitNote = [56, 61, 111, 112, "56", "61", "111", "112"].includes(meta.documentCode);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'detalles':
        return (
          <div className="document-content">
            {/* Document Stamp */}
            <div className="document-stamp">
              <div className="document-stamp-title">
                {translateDocumentSummary(meta?.summary) || documentsDic[parseInt(meta.documentCode)]?.fullName}
              </div>
              <div className="document-stamp-number">
                {dt('detail.docNumberPrefix')} {meta.documentId || meta.number || dt('detail.noInfo')}
              </div>
              <div className="document-stamp-status">
                <PaymentStatusBadge
                  meta={meta}
                  documentType={data?.documentType}
                  companyId={data?.companyId}
                  documentId={data?.id}
                  onUpdated={onDocumentUpdated}
                />
              </div>
            </div>
            
            {/* Main Info Grid */}
            <div className="document-grid">
              <div>
                <div className="info-block">
                  <div className="info-label">{isReceived ? dt('detail.issuedBy') : dt('detail.issuedTo')}</div>
                  <div className="counterpart-name" style={{ fontWeight: 800 }}>
                    {isReceived ? (meta.issuerName || dt('common.noCounterparty')) : meta.receiverName || dt('common.noCounterparty')}
                  </div>
                  <div className="info-value">
                    <span className="secondary" style={{ fontWeight: 600 }}>
                      {formatRut(isReceived ? meta.issuerId : meta.receiverId) || "-"}
                    </span>
                  </div>
                </div>
                <div className="info-row">
                  <div className="info-block">
                    <div className="info-label">{dt('detail.issueDate')}</div>
                    <div className="info-value">
                      {moment(data.documentDate).format("DD/MM/YYYY")}
                    </div>
                  </div>
                  <div className="info-block">
                    <div className="info-label">{dt('detail.dueDate')}</div>
                    <div className="info-value">
                      {data.originalDueDate ? moment(data.originalDueDate).format("DD/MM/YYYY") : dt('detail.noInfo')}
                    </div>
                  </div>
                  <div className="info-block">
                    <div className="info-label">{dt('detail.paymentDate')}</div>
                    <div className="info-value">
                      {data?.meta?.isPaid === true && data.paymentDate ? moment(data.paymentDate.substr(0, 10)).format("DD/MM/YYYY") : dt('detail.noInfo')}
                    </div>
                  </div>
                  {/* Only show rate for DIN documents (documentCode 914) */}
                  {data?.meta?.documentCode === 914 && (
                    <div className="info-block">
                      <div className="info-label">{dt('detail.exchangeRate')}</div>
                      <div className="info-value">
                        {(() => {
                          const rate = rateFromUrl || data?.meta?.rate;
                          if (!rate || rate === "-" || rate === null || rate === undefined) {
                            return dt('detail.noInfo');
                          }
                          
                          // Convert to number and format
                          const numRate = parseFloat(rate);
                          if (isNaN(numRate)) {
                            return rate; // Return as string if not a number
                          }
                          
                          return numRate.toLocaleString('es-CL', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 4
                          });
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Totals Card */}
            <div className="totals-card">
              {/* Exchange information - show first if exists and originalCurrency is not CLP */}
              {meta?.exchange?.originalCurrency && meta.exchange.originalCurrency !== 'CLP' && (
                <>
                  <div className="totals-segment">
                    <div className="totals-label">{dt('detail.originalCurrencyAmount', { currency: meta.exchange.originalCurrency })}</div>
                    <div className="totals-value">
                      {formatMoneyWithCurrency(meta.exchange.originalAmount || 0, meta.exchange.originalCurrency)}
                    </div>
                  </div>
                  {meta.exchange.exchangeRate && (
                    <div className="totals-segment">
                      <div className="totals-label">{dt('detail.exchangeType')}</div>
                      <div className="totals-value">
                        {meta.exchange.exchangeRate}
                      </div>
                    </div>
                  )}
                </>
              )}
              {!meta?.taxableAmount && !meta?.nonTaxableAmount ? (
                <div className="totals-segment">
                  <div className="totals-label">
                    {meta?.taxAmount > 0 ? dt('detail.taxableAmount') : dt('detail.exemptAmount')}
                  </div>
                  <div className="totals-value">
                    {formatMoneyWithCurrency(meta?.netAmount || 0, data.currencyType)}
                  </div>
                </div>
              ) : (
                <>
                  {meta?.taxableAmount > 0 && (
                    <div className="totals-segment">
                      <div className="totals-label">{dt('detail.taxableAmount')}</div>
                      <div className="totals-value">
                        {formatMoneyWithCurrency(meta?.taxableAmount || 0, data.currencyType)}
                      </div>
                    </div>
                  )}
                  {meta?.nonTaxableAmount > 0 && (
                    <div className="totals-segment">
                      <div className="totals-label">{dt('detail.exemptAmount')}</div>
                      <div className="totals-value">
                        {formatMoneyWithCurrency(meta?.nonTaxableAmount || 0, data.currencyType)}
                      </div>
                    </div>
                  )}
                </>
              )}
              <div className="totals-segment">
                <div className="totals-label">{dt('detail.iva', { rate: (meta?.taxRate * 100).toFixed(1) })}</div>
                <div className="totals-value">
                  {formatMoneyWithCurrency(meta?.taxAmount || 0, data.currencyType)}
                </div>
              </div>
              {data.meta?.commonTaxes > 0 && (
                <div className="totals-segment">
                  <div className="totals-label">{dt('detail.commonUseVat')}</div>
                  <div className="totals-value">
                    {formatMoneyWithCurrency(data.meta?.commonTaxes || 0, data.currencyType)}
                  </div>
                </div>
              )}
              {meta?.otherTaxes && meta.otherTaxes.length > 0 && (
                  <div className="totals-segment">
                    <div className="totals-label">{dt('detail.otherTaxes', { rate: otherTaxesRate.toFixed(1) })}</div>
                    <div className="totals-value">
                      {formatMoneyWithCurrency(otherTaxesTotal, data.currencyType)}
                      <div 
                        className="totals-detail-link"
                        onClick={() => setShowOtherTaxesDetails(!showOtherTaxesDetails)}
                      >
                        {dt('detail.seeMoreDetail')} {showOtherTaxesDetails ? '↑' : '↓'}
                      </div>
                    </div>
                    {showOtherTaxesDetails && (
                      <div className="other-taxes-details">
                        {meta.otherTaxes.map((tax, index) => {
                          // Calculate the base amount for tax rate calculation
                          const baseAmount = meta?.taxableAmount || meta?.netAmount || 0;
                          
                          // Calculate tax rate: if taxRate is 0, use tax.value / baseAmount
                          const calculatedTaxRate = tax.taxRate === 0 && baseAmount > 0 
                            ? (tax.value || 0) / baseAmount 
                            : tax.taxRate;
                          
                          return (
                            <div key={index} className="other-tax-item">
                              <div className="other-tax-name">{tax.name} ({(calculatedTaxRate * 100).toFixed(1)}%)</div>
                              <div className="other-tax-value">
                                {formatMoneyWithCurrency(
                                  tax.value || 0, 
                                  data.currencyType
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              {/* Show MONTO (CLP) if exchange exists, otherwise show TOTAL */}
              {meta?.exchange?.originalCurrency && meta.exchange.originalCurrency !== 'CLP' ? (
                <div className="totals-segment">
                  <div className="totals-label">{dt('detail.amountClp')}</div>
                  <div className="totals-value">
                    {formatMoneyWithCurrency(meta.exchange.localAmount || 0, 'CLP')}
                  </div>
                </div>
              ) : (
                <div className="totals-segment">
                  <div className="totals-label">{dt('detail.total')}</div>
                  <div className="totals-value">
                    {formatMoneyWithCurrency(data?.meta?.originalAmount || 0, data.currencyType)}
                  </div>
                </div>
              )}
                
            </div>

            {/* Export Actions */}
            <ExportActions
              documentData={data}
              exportType="rendiciones"
              pdfUrl={meta.download?.pdf?.url || meta.download?.html?.url}
              xmlUrl={meta.download?.xml?.url}
              showPdf={!!(meta.download?.pdf?.url || meta.download?.html?.url)}
              showXml={!!meta.download?.xml?.url}
              showExcel={(() => {
                // Show Excel button only if there's glosa data
                const glosaItems = meta?.description || [];
                const hasValidGlosa = glosaItems.some(item => {
                  const hasDescription = item.description || item.name || item.item;
                  const hasUnitPrice = item.unit_price && item.unit_price !== 0;
                  const hasQuantity = item.quantity && item.quantity !== 0;
                  const hasTotalPrice = item.total_price && item.total_price !== 0;

                  // Must have at least a price (unit or total) to be considered valid
                  if (!hasTotalPrice && !hasUnitPrice) return false;
                  
                  return hasDescription || hasUnitPrice || hasQuantity || hasTotalPrice;
                });
                return hasValidGlosa;
              })()}
            />

            {/* Factoring Notice */}
            {meta?.factoring?.cedido && (
              <FactoringNotice
                issuerName={meta.factoring.issuerName}
                cesionDate={meta.factoring.cesionDate ? moment(meta.factoring.cesionDate).format("DD/MM/YYYY") : null}
                downloadUrl={meta.download?.factoring?.url}
                cesiones={meta.factoring.cesiones}
              />
            )}

            {/* Annulment Notice */}
            {(meta?.isValid === false || meta?.isProtested === true) && (
              <AnnulmentNotice
                text={dt('detail.claimedNotice')}
                downloadUrl={null}
              />
            )}

            {/* No Incluir Notice */}
            {meta?.state === "NO_INCLUIR" && (
              <AnnulmentNotice
                text={dt('detail.noIncludeNotice')}
                downloadUrl={null}
                variant="noIncluir"
              />
            )}

            {/* Credit/Debit Notice */}
            {meta?.refDocuments && (meta?.refDocuments || []).length > 0 &&
              (meta?.refDocuments.map((refDocument) => {
                return (
                  <CreditDebitNotice
                    refDocumentCode={translateDocumentSummary(documentsDic[parseInt(refDocument.documentCode)]?.fullName) || dt('common.document')}
                    refDocumentId={refDocument.documentId}
                    downloadUrl={null}
                />
                )
              })

            )}

            {/* Glosa Section */}
            <div className="glosa-section">
              <div className="info-label">{dt('detail.glosa')}</div>
              {(() => {
                const filteredGlosa = meta?.description?.filter(item => {
                  // Filter out rows where all relevant fields are null/empty
                  const hasDescription = item.description || item.name || item.item;
                  const hasUnitPrice = item.unit_price && item.unit_price !== 0;
                  const hasQuantity = item.quantity && item.quantity !== 0;
                  const hasTotalPrice = item.total_price && item.total_price !== 0;

                  if (!hasTotalPrice && !hasUnitPrice) return false
                  
                  return hasDescription || hasUnitPrice || hasQuantity || hasTotalPrice;
                }) || [];
                const hasNotes = meta?.notes?.length > 0;

                return (
                  <div className="glosa-content-wrapper">
                    {filteredGlosa.length > 0 && (
                      <div className="glosa-table">
                        <div className="glosa-items">
                          {filteredGlosa.map((item, index) => (
                            <div key={index} className="glosa-item">
                              <div className="glosa-item-description">
                                {item.name?.trim() && item.description?.trim() ? (
                                  <>
                                    <div>{item.name.trim()}</div>
                                    <div className="glosa-item-subdescription">{item.description.trim()}</div>
                                  </>
                                ) : (
                                  item.name?.trim() || item.description?.trim() || item.item || dt('detail.noGlosaEntered')
                                )}
                              </div>
                              <div className="glosa-item-metrics">
                                <div className="glosa-metric">
                                  <div className="glosa-metric-label">{dt('detail.unitPrice')}</div>
                                  <div className="glosa-metric-value">
                                    {formatMoneyWithCurrency(
                                      item.unit_price || 0, 
                                      data.currencyType,
                                      true,
                                      true
                                    )}
                                  </div>
                                </div>
                                <div className="glosa-metric">
                                  <div className="glosa-metric-label">{dt('detail.quantity')}</div>
                                  <div className="glosa-metric-value">
                                    {item.quantity || 1}
                                  </div>
                                </div>
                                <div className="glosa-metric">
                                  <div className="glosa-metric-label">{dt('detail.totalLabel')}</div>
                                  <div className="glosa-metric-value">
                                    {formatMoneyWithCurrency(
                                      item.total_price || (item.unit_price * (item.quantity || 1)) || 0, 
                                      data.currencyType,
                                      true,
                                      true
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {hasNotes && (
                      <div className="notes-section">
                        <div className="info-label">{dt('detail.notes')}</div>
                        <div className="glosa-box">
                          <div className="glosa-content">
                            {meta.notes.map((note, index) => (
                              <div key={index}>{note.text}</div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    {filteredGlosa.length === 0 && !hasNotes && (
                      <div className="glosa-empty">
                        {dt('detail.noGlosa')}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        );
      case 'matches':
        return (
          <MatchesTab
            data={data}
            matchesData={meta?.matchs}
          />
        );
      case 'nominas':
        return (
          <NominasTab
            data={data}
          />
        );
      case 'parametrizacion':
        return (
          <ParametrizacionTab
            data={data}
          />
        );
      case 'comentarios':
        return (
          <CommentsTab
            data={data}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="document-detail-container">
      {/* Tabs */}
      <FilterTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        className="document-filter-tabs"
      />

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default TaxDocumentDetail; 